import React, { useEffect } from 'react';
import { ShieldCheck, Truck, HeadphonesIcon, RefreshCcw, Wallet, Search, Sparkles, TrendingUp, HeartHandshake, Building2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AboutPageProps {
    isDark: boolean;
}

export const AboutPage: React.FC<AboutPageProps> = ({ isDark }) => {
    const navigate = useNavigate();

    const features = [
        { icon: <Truck className="w-6 h-6" />, title: "خدمة توصيل سريعة", desc: "أسرع دليفري في رشيد", color: "from-blue-400 to-blue-600" },
        { icon: <ShieldCheck className="w-6 h-6" />, title: "مراجعات و معلومات", desc: "تفاصيل وحالة كل منتج", color: "from-emerald-400 to-emerald-600" },
        { icon: <Sparkles className="w-6 h-6" />, title: "عروض حصرية", desc: "خصومات غير مسبوقة", color: "from-amber-400 to-orange-500" },
        { icon: <Wallet className="w-6 h-6" />, title: "نظام كاش باك", desc: "نقط مشتريات مميزة", color: "from-purple-400 to-purple-600" },
        { icon: <HeadphonesIcon className="w-6 h-6" />, title: "خدمة عملاء", desc: "دعم على مدار الساعة", color: "from-pink-400 to-rose-500" },
        { icon: <RefreshCcw className="w-6 h-6" />, title: "خدمة استرجاع", desc: "سياسة استبدال مرنة", color: "from-cyan-400 to-cyan-600" },
    ];

    const goals = [
        "إضافة مزايا جديدة قريباً مثل مقارنة المنتجات حسب السعر.",
        "الاشتراك في نظام كاش باك متطور يخدم البائع والمشتري.",
        "توفير أدوات متقدمة لتسهيل عملية البحث والشراء.",
        "أن يصبح «رشيد أونلاين» الوجهة الأولى للتسوق داخل المدينة."
    ];

    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);

        const observerOptions = {
            threshold: 0.15,
            rootMargin: "0px 0px -50px 0px"
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    // Add continuous draw animation class to paths when timeline wrapper enters
                    if (entry.target.classList.contains('timeline-wrapper')) {
                        const path = entry.target.querySelector('path');
                        if (path) path.classList.add('draw-path');
                    }
                    observer.unobserve(entry.target); // Play animation once
                }
            });
        }, observerOptions);

        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <div className={`min-h-screen w-full pb-[72px] pt-28 md:pt-36 relative overflow-hidden`} style={{ direction: 'rtl' }}>

            {/* Back Button */}
            <div className="fixed top-24 md:top-32 right-4 md:right-8 z-[60] animate-fade-in">
                <button
                    onClick={() => navigate(-1)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-black text-sm transition-all active:scale-95 shadow-lg backdrop-blur-md border ${isDark ? 'bg-white/10 border-white/10 text-white hover:bg-white/20' : 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white'}`}
                >
                    <ArrowRight size={20} />
                    <span>رجوع</span>
                </button>
            </div>

            {/* Blurred Background Image */}
            <div
                className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none scale-110"
                style={{
                    backgroundImage: `linear-gradient(rgba(0,0,0,${isDark ? '0.75' : '0.15'}), rgba(0,0,0,${isDark ? '0.75' : '0.15'})), url('https://raw.githubusercontent.com/yousefkatato11-creator/main/main/main.jpg')`,
                    filter: 'blur(3px)',
                }}
            />
            {/* Content overlay to ensure readability */}
            <div className={`absolute inset-0 z-0 ${isDark ? 'bg-[#0f1115]/60' : 'bg-[#f8f9ff]/70'} transition-colors duration-700 pointer-events-none`} />

            {/* Inner scroll container */}
            <div className="relative z-10 w-full">

                {/* 1. Hero Title Area */}
                <div className="max-w-7xl mx-auto px-4 md:px-8 mb-16 relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-blue-500/20 blur-[100px] rounded-full pointer-events-none"></div>
                    <div className="text-center space-y-4 animate-on-scroll zoom-in">
                        <div className="inline-block px-4 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-600 shadow-lg backdrop-blur-md text-xs md:text-sm font-black mb-2 animate-pulse">
                            تعرف علينا
                        </div>
                        <h1 className={`text-4xl md:text-7xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} leading-tight`}>
                            رشيد <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600 drop-shadow-lg">أونلاين</span>
                        </h1>
                        <p className={`text-base md:text-2xl font-bold opacity-90 drop-shadow-md ${isDark ? 'text-blue-100' : 'text-slate-700'}`}>
                            منصة التسوق المحلية الأقرب والأفضل إليك
                        </p>
                    </div>
                </div>

                {/* 2. What is Rashid Online? - 4 Points Flow */}
                <div className="max-w-7xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
                    <div className="text-center mb-10 md:mb-16 animate-on-scroll zoom-in">
                        <div className="inline-flex items-center justify-center gap-3 mb-4">
                            <Building2 className="w-6 h-6 md:w-8 md:h-8 text-[#5ba1f5] animate-pulse" />
                            <h2 className="text-2xl md:text-5xl font-black text-[#5ba1f5] drop-shadow-lg">
                                ما هو رشيد أونلاين؟
                            </h2>
                        </div>
                        <p className={`text-sm md:text-xl font-bold ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                            موقع محلي صُمم خصيصاً لتسهيل عملية البيع والشراء داخل مدينة رشيد
                        </p>
                    </div>

                    <div className="relative w-full py-10 animate-on-scroll timeline-wrapper">

                        {/* The SVG Winding Path background Horizontal Desktop only */}
                        <div className="absolute inset-x-0 hidden md:block" style={{ top: '3.5rem', zIndex: 0 }}>
                            <svg className="w-full h-24" preserveAspectRatio="none" viewBox="0 0 100 20" style={{ overflow: 'visible' }}>
                                {/* Curve passing horizontally through the circles. RTL: Starts Right (95), goes Left (5) */}
                                <path
                                    d="M 95,10 C 75,25 60,-5 50,10 C 40,25 25,-5 5,10"
                                    fill="none"
                                    stroke="url(#gradient-horizontal)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeDasharray="4 4"
                                    className="path-animated opacity-60"
                                />
                                <defs>
                                    <linearGradient id="gradient-horizontal" x1="100%" y1="0%" x2="0%" y2="0%">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                                        <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#ec4899" stopOpacity="0.4" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>

                        {/* Mobile straight line fallback in center */}
                        <div className={`absolute top-0 bottom-0 right-1/2 translate-x-1/2 w-0.5 md:hidden rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/30'} z-0`}></div>

                        <div className="flex flex-col md:flex-row gap-8 md:gap-6 relative z-10 w-full justify-between items-start">
                            {[
                                { title: "موقع محلي", desc: "صُمم خصيصاً في رشيد لتسهيل عملية البيع والشراء.", icon: <Building2 className="w-6 h-6 md:w-8 md:h-8" /> },
                                { title: "تسوق من مكانك", desc: "تصفح منتجات المحلات وأنت في مكانك بدون رسوم إضافية.", icon: <Search className="w-6 h-6 md:w-8 md:h-8" /> },
                                { title: "بدون مجهود", desc: "لا داعي لإضاعة الوقت، اطلب كل اللي تحتاجه بضغطة زر.", icon: <RefreshCcw className="w-6 h-6 md:w-8 md:h-8" /> },
                                { title: "أفضل العروض", desc: "نوصلك لأفضل العروض وأرخص الأسعار بأسرع طريقة ممكنة.", icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8" /> }
                            ].map((point, idx) => (
                                <div key={idx} className="animate-on-scroll slide-up flex flex-col items-center text-center group w-full md:w-1/4 px-2" style={{ transitionDelay: `${(idx * 1500) + 500}ms` }}>

                                    {/* Number Bubble centered above text */}
                                    <div className="relative mb-4 md:mb-6">
                                        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border-[3px] transition-all duration-500 shadow-xl z-20 relative ${isDark ? 'bg-[#1e2230] border-[#5ba1f5] text-[#5ba1f5] group-hover:border-[#e881fa] group-hover:text-[#e881fa] group-hover:shadow-[0_0_25px_rgba(232,121,249,0.5)] group-hover:-translate-y-2' : 'bg-white border-blue-500 text-blue-500 group-hover:border-purple-500 group-hover:text-purple-500 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] group-hover:-translate-y-2'}`}>
                                            {point.icon}
                                        </div>
                                        {/* Pulse ring beneath the circle */}
                                        <div className="absolute inset-0 rounded-full border border-blue-500/50 scale-[1.3] animate-ping opacity-30 z-10"></div>
                                    </div>

                                    {/* Text Box styled as glowing node info */}
                                    <div className={`p-5 md:p-6 w-full rounded-[1.5rem] backdrop-blur-xl border transition-all duration-500 group-hover:-translate-y-2 ${isDark ? 'bg-[#2a2c33]/90 border-white/5 hover:bg-[#2a2c33] hover:border-white/20 hover:shadow-[0_10px_30px_rgba(59,130,246,0.15)]' : 'bg-white/80 border-white/50 shadow-xl hover:bg-white hover:shadow-[0_10px_30px_rgba(59,130,246,0.2)]'} text-center h-full flex flex-col justify-start`}>
                                        <h3 className={`text-base md:text-lg font-black mb-2 md:mb-3 ${isDark ? 'text-[#5ba1f5] group-hover:text-[#e881fa] transition-colors duration-300' : 'text-blue-600 group-hover:text-purple-600 transition-colors duration-300'}`}>
                                            {point.title}
                                        </h3>
                                        <p className={`text-[12px] md:text-sm font-bold leading-relaxed md:leading-loose ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                                            {point.desc}
                                        </p>
                                    </div>

                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 3. Premium Services Grid */}
                <div className="max-w-7xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
                    <div className="text-center mb-10 md:mb-12 animate-on-scroll slide-up">
                        <h2 className={`text-2xl md:text-4xl font-black mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            خدماتنا المميزة
                        </h2>
                        <p className={`text-sm md:text-lg font-bold opacity-80 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            نقدم باقة من الخدمات لدعم المشترين وتوفير أفضل تجربة تسوق:
                        </p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 md:gap-6">
                        {features.map((item, idx) => (
                            <div
                                key={idx}
                                className={`animate-on-scroll slide-up flex flex-col items-center justify-center p-6 rounded-3xl text-center shadow-lg transition-all duration-300 hover:-translate-y-2 border ${isDark ? 'bg-white/10 border-white/20 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]' : 'bg-white/80 backdrop-blur-lg border-white/50 hover:shadow-xl'}`}
                                style={{ transitionDelay: `${idx * 100}ms` }}
                            >
                                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full mb-4 flex items-center justify-center bg-gradient-to-tr ${item.color} text-white shadow-lg`}>
                                    {item.icon}
                                </div>
                                <h3 className={`text-sm md:text-base font-black mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                    {item.title}
                                </h3>
                                <p className={`text-[10px] md:text-xs font-bold leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. Future Goals - Winding Curved Path Timeline */}
                <div className="max-w-5xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
                    <div className="text-center mb-10 md:mb-16 animate-on-scroll zoom-in">
                        <div className="inline-flex items-center justify-center gap-3 mb-4">
                            <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blue-500 animate-pulse" />
                            <h2 className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                نحن نتطور باستمرار 🚀
                            </h2>
                        </div>
                        <p className={`text-sm md:text-lg font-bold opacity-80 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            خارطة الطريق لتحسين تجربتك الشرائية معنا
                        </p>
                    </div>

                    {/* Timeline Container */}
                    <div className="relative w-full max-w-4xl mx-auto py-10 animate-on-scroll slide-up timeline-wrapper">

                        {/* The SVG S-Curve Path background Desktop only */}
                        <div className="absolute inset-x-0 hidden md:block" style={{ top: '80px', bottom: '80px', zIndex: 0 }}>
                            <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" style={{ overflow: 'visible' }}>
                                {/* 
                                    S-Curve:
                                    Starts Top Center (50, 4)
                                    Leaves left, crosses center (50, 33)
                                    Loops right, crosses center (50, 66)
                                    Loops left, ends center (50, 96)
                                */}
                                <path
                                    d="M 50,4 C 10,10 10,25 50,33 C 90,41 90,56 50,66 C 10,76 10,90 50,96"
                                    fill="none"
                                    stroke="url(#gradient)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    className="path-animated-vertical opacity-60"
                                    pathLength="100"
                                />
                                <defs>
                                    <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                                        <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#ec4899" stopOpacity="0.4" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>

                        {/* Mobile straight line fallback in center */}
                        <div className={`absolute top-0 bottom-0 right-1/2 translate-x-1/2 w-0.5 md:hidden rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/30'} z-0`}></div>

                        <div className="flex flex-col space-y-16 md:space-y-24 relative z-10 w-full items-center">
                            {goals.map((goal, idx) => {
                                // Transition delays perfectly synced to the S-Curve drawing speed over 4 seconds
                                const delays = [300, 2000, 3400, 4095];
                                const delay = delays[idx] || 0;

                                return (
                                    <div key={idx} className={`flex w-full justify-center lg:w-3/4 animate-on-scroll slide-up`} style={{ transitionDelay: `${delay}ms` }}>

                                        <div className={`flex flex-col items-center gap-3 md:gap-4 text-center group w-full px-4`}>

                                            {/* Number Bubble centered above text */}
                                            <div className="relative group hover:-translate-y-2 transition-transform duration-300">
                                                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center border-[3px] transition-all duration-300 shadow-xl z-20 relative ${isDark ? 'bg-[#1e2230] border-blue-500 group-hover:border-purple-400 group-hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] group-hover:scale-110' : 'bg-white border-blue-500 group-hover:border-purple-500 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] group-hover:scale-110'}`}>
                                                    <span className={`text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500`}>
                                                        {idx + 1}
                                                    </span>
                                                </div>
                                                {/* Pulse ring beneath the circle */}
                                                <div className="absolute inset-0 rounded-full border border-blue-500/50 scale-[1.3] animate-ping opacity-50 z-10"></div>
                                            </div>

                                            {/* Text Box styled as glowing node info */}
                                            <div className={`p-4 md:p-6 w-full max-w-sm rounded-[1.5rem] backdrop-blur-xl border transition-all duration-500 group-hover:-translate-y-2 ${isDark ? 'bg-[#2a2c33]/90 border-white/5 hover:bg-[#2a2c33] hover:border-white/20 hover:shadow-[0_10px_30px_rgba(59,130,246,0.2)]' : 'bg-white/80 border-white/50 shadow-xl hover:bg-white hover:shadow-[0_10px_30px_rgba(59,130,246,0.3)]'} text-center mt-2`}>
                                                <p className={`text-sm md:text-base font-bold leading-relaxed ${isDark ? 'text-white/95' : 'text-slate-800'}`}>
                                                    {goal}
                                                </p>
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

            </div>

            {/* Custom Styles overrides for floating animations & Scroll animations */}
            <style>{`
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        /* SVG Line drawing CSS */
        .path-animated {
            stroke-dasharray: 200;
            stroke-dashoffset: 200;
        }
        .path-animated.draw-path {
            animation: dashDraw 6s linear forwards;
        }
        .path-animated-vertical {
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
        }
        .path-animated-vertical.draw-path {
            animation: dashDraw 6s linear forwards;
        }
        @keyframes dashDraw {
            to {
                stroke-dashoffset: 0;
            }
        }

        /* Scroll Animations */
        .animate-on-scroll {
            opacity: 0;
            will-change: opacity, transform;
            transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-on-scroll.slide-up {
            transform: translateY(60px);
        }
        .animate-on-scroll.slide-right {
            transform: translateX(80px); /* Because direction: rtl, positive X comes from the visual 'right' */
        }
        .animate-on-scroll.slide-left {
            transform: translateX(-80px); /* Negative X comes from visual 'left' */
        }
        .animate-on-scroll.zoom-in {
            transform: scale(0.85) translateY(30px);
        }
        .animate-on-scroll.is-visible {
            opacity: 1 !important;
            transform: translate(0) scale(1) !important;
        }
      `}</style>
        </div>
    );
};
